<template>
  <div class="user-orders-container">
    <!-- 搜索区域 -->
    <div class="search-area">
      <el-row :gutter="20" align="middle">
        <el-col :span="18">
          <el-space>
            <!-- 搜索框 -->
            <el-input
                v-model="searchForm.keyword"
                placeholder="搜索订单编号、插件名称"
                style="width: 300px"
                clearable
                :prefix-icon="Search"
                @clear="clearSearch"
                @keyup.enter="handleSearch"
            />
            <el-button type="primary" @click="handleSearch">搜索</el-button>
          </el-space>
        </el-col>
      </el-row>
    </div>

    <!-- 表格区域 -->
    <el-table
        :data="tableData"
        v-loading="loading"
        class="order-table"
        stripe
        style="width: 100%"
        border
        empty-text="暂无相关信息"
    >
      <el-table-column prop="orderNo" label="订单编号"  />
      <el-table-column prop="productCode" label="插件编码"  />
      <el-table-column prop="productName" label="插件名称"  />
      <el-table-column prop="totalFee" label="金额（元）" width="100">
        <template #default="scope">
          <span style="color: #f56c6c; font-weight: 500;">
              ¥{{ scope.row.totalFee?.toFixed(2) }}
          </span>
        </template>
      </el-table-column>
      <el-table-column prop="purchasedDays" label="时长(天)" width="80" />
      <el-table-column prop="paidAt" label="购买时间"  >
        <template #default="scope">
          {{ scope.row.paidAt ? formatDateTime(scope.row.paidAt) : '-' }}
        </template>
      </el-table-column>
      <el-table-column prop="paymentMethodName" label="支付方式" width="100" />
      <el-table-column prop="paymentStatus" label="状态" >
        <template #default="scope">
          <el-tag :type="getStatusTagType(scope.row.paymentStatus)">
            {{ formatStatus(scope.row.paymentStatus) }}
          </el-tag>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页组件 -->
    <div class="pagination-container">
      <el-pagination
          v-model:current-page="currentPage"
          :page-size="pageSize"
          :total="total"
          :pager-count="5"
          layout="prev, pager, next"
          @current-change="handleCurrentChange"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive } from 'vue'
import { ElMessage } from 'element-plus'
import { Search } from '@element-plus/icons-vue'
import request from '@/utils/request'

// 响应式数据
const loading = ref(false)
const tableData = ref([])

// 分页相关数据
const currentPage = ref(1)
const pageSize = ref(8)
const total = ref(0)

// 搜索条件
const searchForm = reactive({
  keyword: ''
})

// 获取订单列表
const getOrderList = async () => {
  loading.value = true
  try {
    const params = {
      page: currentPage.value,
      size: pageSize.value
    }

    const response = await request.get('/order/all', { params })
    tableData.value = response.data.list
    total.value = response.data.total
  } catch (error) {
    if (error.response && error.response.status === 403) {
      // ElMessage.error('权限不足，无法查看订单')
    } else {
      // ElMessage.error('获取订单列表失败')
    }
    console.error(error)
  } finally {
    loading.value = false
  }
}

// 清空搜索
const clearSearch = () => {
  searchForm.keyword = ''
  handleSearch()
}

// 搜索订单
const handleSearch = async () => {
  if (!searchForm.keyword) {
    resetSearch()
    return
  }

  loading.value = true
  try {
    const params = {
      keyword: searchForm.keyword,
      page: currentPage.value,
      size: pageSize.value
    }

    const response = await request.get('/order/search', { params })
    tableData.value = response.data.list
    total.value = response.data.total

    if (tableData.value.length === 0) {
      ElMessage.info('未找到相关订单')
    } else {
      ElMessage.success(`找到 ${tableData.value.length} 条订单`)
    }
  } catch (error) {
    if (error.response && error.response.status === 403) {
      // ElMessage.error('权限不足，无法搜索订单')
    } else if (error.response && error.response.status === 400) {
      // ElMessage.error(error.response.data?.message || '搜索参数错误')
    } else {
      // ElMessage.error('搜索订单失败')
    }
    console.error(error)
  } finally {
    loading.value = false
  }
}

// 重置搜索
const resetSearch = () => {
  searchForm.keyword = ''
  currentPage.value = 1
  getOrderList()
}

// 当前页改变
const handleCurrentChange = (newPage) => {
  currentPage.value = newPage
  if (searchForm.keyword) {
    handleSearch()
  } else {
    getOrderList()
  }
}

// 支付状态格式化
const formatStatus = (status) => {
  const statusMap = {
    0: '未支付',
    1: '完成',
    2: '已退款',
    3: '已关闭'
  }
  return statusMap[status] || '未知'
}

// 状态标签类型
const getStatusTagType = (status) => {
  const typeMap = {
    0: 'warning',  // 未支付 - 黄色
    1: 'success',  // 完成 - 绿色
    2: 'info',     // 已退款 - 蓝色
    3: 'danger'    // 已关闭 - 红色
  }
  return typeMap[status] || 'info'
}

// 格式化日期时间
const formatDateTime = (dateTimeStr) => {
  if (!dateTimeStr) return '-'
  const date = new Date(dateTimeStr)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

onMounted(() => {
  getOrderList()
})
</script>

<style scoped>
.user-orders-container {
  padding: 20px;
  margin: 0 auto;
}

/* 搜索区域样式 */
.search-area {
  margin-bottom: 20px;
  border-radius: 4px;
}

/* 表格样式 */
:deep(.el-table__header) {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue', Helvetica, Arial, sans-serif;
}

:deep(.el-table__header th) {
  font-weight: 600;
  color: #1f2937;
  background-color: #f8fafc !important;
  font-size: 14px;
  letter-spacing: 0.025em;
}

:deep(.el-table__header .cell) {
  font-weight: 600;
  color: #374151;
}

:deep(.el-table th),
:deep(.el-table td) {
  text-align: center;
  border-bottom: 1px solid #f3f4f6;
}

/* 自定义空数据样式 */
:deep(.el-table__empty-block) {
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.el-table__empty-text) {
  color: #909399;
  font-size: 14px;
  font-weight: 500;
}

/* 分页样式 */
.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
}

:deep(.el-pagination) {
  font-weight: normal;
}
</style>